import React, { useState, useEffect } from 'react';
import { 
  X, 
  MapPin, 
  Clock, 
  Activity, 
  Gauge, 
  Navigation,
  Calendar,
  Route,
  AlertCircle,
  Download
} from 'lucide-react';
import { TagBlac, TrazabilidadPunto } from '../../interfaces/TagBlacInterface';
import { getTagTrazabilidad } from '../../apis/blacApi';
import { tracksolidApi } from '../../apis/tracksolidApi';
import { exportTagWithTrazabilidad } from '../../utils/excelExport';

interface TagDetailsModalProps {
  tag: TagBlac & { fuente?: 'blac' | 'tracksolid'; tracksolid_id?: number };
  isOpen: boolean;
  onClose: () => void;
}

export const TagDetailsModal: React.FC<TagDetailsModalProps> = ({ tag, isOpen, onClose }) => {
  const [trazabilidad, setTrazabilidad] = useState<TrazabilidadPunto[]>([]);
  const [loadingTrazabilidad, setLoadingTrazabilidad] = useState(false);
  const [activeTab, setActiveTab] = useState<'info' | 'trazabilidad'>('info');

  useEffect(() => {
    if (isOpen && activeTab === 'trazabilidad') {
      loadTrazabilidad();
    }
  }, [isOpen, activeTab, tag.id]);

  const loadTrazabilidad = async () => {
    try {
      setLoadingTrazabilidad(true);
      
      if (tag.fuente === 'tracksolid') {
        const trackId = tag.tracksolid_id || tag.id;
        const response = await tracksolidApi.getTrazabilidad(trackId);
        // Normalizar al formato TrazabilidadPunto
        const puntos: TrazabilidadPunto[] = (response.trazabilidad || []).map(p => ({
          id: p.id,
          longitude: p.longitude,
          latitude: p.latitude,
          speed: p.speed || 0,
          odometer: 0,
          event: p.event,
          dateTime_blac: p.dateTime_tracksolid || '',
          fecha_registro: p.fecha_registro,
          distancia_recorrida: p.distancia_recorrida,
          tiempo_transcurrido: p.tiempo_transcurrido,
          velocidad_promedio: p.velocidad_promedio,
          tipo_evento: p.tipo_evento as TrazabilidadPunto['tipo_evento'],
        }));
        setTrazabilidad(puntos);
      } else {
        const response = await getTagTrazabilidad(tag.id);
        setTrazabilidad(response.trazabilidad);
      }
    } catch (error) {
      console.error('Error cargando trazabilidad:', error);
    } finally {
      setLoadingTrazabilidad(false);
    }
  };

  const formatDateTime = (dateTime?: string) => {
    if (!dateTime) return 'N/A';
    
    if (dateTime.length === 14) {
      const year = dateTime.substring(0, 4);
      const month = dateTime.substring(4, 6);
      const day = dateTime.substring(6, 8);
      const hour = dateTime.substring(8, 10);
      const minute = dateTime.substring(10, 12);
      const second = dateTime.substring(12, 14);
      
      return `${day}/${month}/${year} ${hour}:${minute}:${second}`;
    }
    
    try {
      return new Date(dateTime).toLocaleString('es-MX');
    } catch {
      return dateTime;
    }
  };

  // Helper function para convertir valores a número de manera segura
  const safeNumber = (value: number | string | undefined): number => {
    if (value === undefined || value === null) return 0;
    const num = typeof value === 'string' ? parseFloat(value) : value;
    return isNaN(num) ? 0 : num;
  };

  const getEventIcon = (event?: string) => {
    if (!event) return <Activity size={16} />;
    
    switch (event.toLowerCase()) {
      case 'gps':
      case 'posicion':
        return <Navigation size={16} />;
      case 'stop':
      case 'parada':
        return <AlertCircle size={16} />;
      default:
        return <Activity size={16} />;
    }
  };

  if (!isOpen) return null;

  const styles = `
    .modal-overlay {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.8);
      backdrop-filter: blur(8px);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 1000;
      padding: 1rem;
    }

    .modal-content {
      background: linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%);
      border: 1px solid rgba(255, 255, 255, 0.1);
      border-radius: 20px;
      width: 100%;
      max-width: 800px;
      max-height: 90vh;
      overflow: hidden;
      color: white;
      font-family: 'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
    }

    .modal-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 1.5rem 2rem;
      border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    }

    .modal-title {
      font-size: 1.5rem;
      font-weight: 700;
      color: #007AFF;
    }

    .close-button {
      background: rgba(255, 255, 255, 0.1);
      border: none;
      border-radius: 8px;
      padding: 8px;
      color: white;
      cursor: pointer;
      transition: all 0.3s ease;
    }

    .close-button:hover {
      background: rgba(255, 255, 255, 0.2);
    }

    .modal-tabs {
      display: flex;
      border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    }

    .tab-button {
      flex: 1;
      padding: 1rem 2rem;
      background: transparent;
      border: none;
      color: rgba(255, 255, 255, 0.6);
      font-size: 1rem;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.3s ease;
      border-bottom: 2px solid transparent;
    }

    .tab-button.active {
      color: #007AFF;
      border-bottom-color: #007AFF;
    }

    .tab-button:hover {
      color: rgba(255, 255, 255, 0.8);
    }

    .modal-body {
      padding: 2rem;
      max-height: 60vh;
      overflow-y: auto;
    }

    .info-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
      gap: 1.5rem;
    }

    .info-card {
      background: rgba(255, 255, 255, 0.05);
      border: 1px solid rgba(255, 255, 255, 0.1);
      border-radius: 12px;
      padding: 1.5rem;
    }

    .info-header {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      margin-bottom: 1rem;
      color: #007AFF;
      font-weight: 600;
    }

    .info-item {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 0.5rem 0;
      border-bottom: 1px solid rgba(255, 255, 255, 0.05);
    }

    .info-item:last-child {
      border-bottom: none;
    }

    .info-label {
      color: rgba(255, 255, 255, 0.7);
      font-size: 0.9rem;
    }

    .info-value {
      color: white;
      font-weight: 500;
      text-align: right;
    }

    .trazabilidad-list {
      display: flex;
      flex-direction: column;
      gap: 1rem;
    }

    .trazabilidad-item {
      background: rgba(255, 255, 255, 0.05);
      border: 1px solid rgba(255, 255, 255, 0.1);
      border-radius: 12px;
      padding: 1rem;
      transition: all 0.3s ease;
    }

    .trazabilidad-item:hover {
      background: rgba(255, 255, 255, 0.08);
    }

    .trazabilidad-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 0.5rem;
    }

    .trazabilidad-event {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      color: #007AFF;
      font-weight: 500;
    }

    .trazabilidad-time {
      color: rgba(255, 255, 255, 0.6);
      font-size: 0.9rem;
    }

    .trazabilidad-details {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
      gap: 1rem;
      margin-top: 1rem;
    }

    .detail-item {
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
    }

    .detail-label {
      color: rgba(255, 255, 255, 0.6);
      font-size: 0.8rem;
    }

    .detail-value {
      color: white;
      font-weight: 500;
    }

    .loading-state {
      display: flex;
      justify-content: center;
      align-items: center;
      padding: 3rem;
      color: rgba(255, 255, 255, 0.6);
    }

    .spinner {
      width: 32px;
      height: 32px;
      border: 2px solid rgba(255, 255, 255, 0.1);
      border-top: 2px solid #007AFF;
      border-radius: 50%;
      animation: spin 1s linear infinite;
      margin-right: 1rem;
    }

    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }

    .empty-state {
      text-align: center;
      padding: 3rem;
      color: rgba(255, 255, 255, 0.6);
    }

    /* Scrollbar personalizado */
    .modal-body::-webkit-scrollbar {
      width: 6px;
    }

    .modal-body::-webkit-scrollbar-track {
      background: rgba(255, 255, 255, 0.1);
      border-radius: 3px;
    }

    .modal-body::-webkit-scrollbar-thumb {
      background: rgba(0, 122, 255, 0.5);
      border-radius: 3px;
    }

    .modal-body::-webkit-scrollbar-thumb:hover {
      background: rgba(0, 122, 255, 0.7);
    }

    @media (max-width: 768px) {
      .modal-content {
        margin: 1rem;
        max-height: 95vh;
      }

      .modal-header {
        padding: 1rem;
      }

      .modal-body {
        padding: 1rem;
      }

      .info-grid {
        grid-template-columns: 1fr;
      }

      .trazabilidad-details {
        grid-template-columns: 1fr;
      }
    }
  `;

  return (
    <>
      <style>{styles}</style>
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div className="modal-header">
            <h2 className="modal-title">{tag.name_device}</h2>
            <button onClick={onClose} className="close-button">
              <X size={20} />
            </button>
          </div>

          {/* Tabs */}
          <div className="modal-tabs">
            <button
              className={`tab-button ${activeTab === 'info' ? 'active' : ''}`}
              onClick={() => setActiveTab('info')}
            >
              Información
            </button>
            <button
              className={`tab-button ${activeTab === 'trazabilidad' ? 'active' : ''}`}
              onClick={() => setActiveTab('trazabilidad')}
            >
              Trazabilidad
            </button>
          </div>

          {/* Body */}
          <div className="modal-body">
            {activeTab === 'info' && (
              <div className="info-grid">
                {/* Información General */}
                <div className="info-card">
                  <div className="info-header">
                    <Activity size={20} />
                    Información General
                  </div>
                  <div className="info-item">
                    <span className="info-label">Nombre</span>
                    <span className="info-value">{tag.plate}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Estado</span>
                    <span className="info-value" style={{ 
                      color: tag.estatus === 'activo' ? '#10B981' : '#EF4444' 
                    }}>
                      {tag.estatus}
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Último Evento</span>
                    <span className="info-value">{tag.event || 'N/A'}</span>
                  </div>
                </div>

                {/* Posición Actual */}
                <div className="info-card">
                  <div className="info-header">
                    <MapPin size={20} />
                    Posición Actual
                  </div>
                  <div className="info-item">
                    <span className="info-label">Latitud</span>
                    <span className="info-value">
                      {tag.latitude ? safeNumber(tag.latitude).toFixed(6) : 'N/A'}
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Longitud</span>
                    <span className="info-value">
                      {tag.longitude ? safeNumber(tag.longitude).toFixed(6) : 'N/A'}
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Dirección</span>
                    <span className="info-value" style={{ fontSize: '0.8rem' }}>
                      {tag.address || 'N/A'}
                    </span>
                  </div>
                </div>

                {/* Fechas */}
                <div className="info-card">
                  <div className="info-header">
                    <Clock size={20} />
                    Fechas
                  </div>
                  <div className="info-item">
                    <span className="info-label">Último Reporte</span>
                    <span className="info-value" style={{ fontSize: '0.8rem' }}>
                      {formatDateTime(tag.dateTime)}
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Última Actualización</span>
                    <span className="info-value" style={{ fontSize: '0.8rem' }}>
                      {formatDateTime(tag.ultima_actualizacion)}
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Creado</span>
                    <span className="info-value" style={{ fontSize: '0.8rem' }}>
                      {formatDateTime(tag.created_at)}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'trazabilidad' && (
              <div>
                {loadingTrazabilidad ? (
                  <div className="loading-state">
                    <div className="spinner" />
                    Cargando trazabilidad...
                  </div>
                ) : trazabilidad.length === 0 ? (
                  <div className="empty-state">
                    <Route size={48} style={{ opacity: 0.5, margin: '0 auto 1rem' }} />
                    <h3>Sin datos de trazabilidad</h3>
                    <p>No hay puntos de trazabilidad registrados para este tag</p>
                  </div>
                ) : (
                  <div className="trazabilidad-list">
                    <div style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      marginBottom: '1rem', padding: '0.75rem 1rem',
                      background: 'rgba(255,255,255,0.05)', borderRadius: '10px',
                      border: '1px solid rgba(255,255,255,0.1)',
                    }}>
                      <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.9rem' }}>
                        {trazabilidad.length} punto{trazabilidad.length !== 1 ? 's' : ''} de trazabilidad
                      </span>
                      <button
                        onClick={() => exportTagWithTrazabilidad(tag, trazabilidad)}
                        style={{
                          display: 'flex', alignItems: 'center', gap: '6px',
                          padding: '8px 16px', borderRadius: '8px', border: 'none',
                          background: 'linear-gradient(135deg, #007AFF 0%, #0056CC 100%)',
                          color: 'white', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 500,
                          transition: 'all 0.3s ease',
                        }}
                      >
                        <Download size={14} />
                        Descargar Excel
                      </button>
                    </div>
                    {trazabilidad.slice(0, 10).map((punto, index) => (
                      <div key={punto.id} className="trazabilidad-item">
                        <div className="trazabilidad-header">
                          <div className="trazabilidad-event">
                            {getEventIcon(punto.event)}
                            {punto.event || 'Posición'}
                          </div>
                          <div className="trazabilidad-time">
                            {formatDateTime(punto.dateTime_blac)}
                          </div>
                        </div>
                        
                        <div className="trazabilidad-details">
                          <div className="detail-item">
                            <span className="detail-label">Coordenadas</span>
                            <span className="detail-value">
                              {safeNumber(punto.latitude).toFixed(6)}, {safeNumber(punto.longitude).toFixed(6)}
                            </span>
                          </div>
                          {punto.distancia_recorrida && (
                            <div className="detail-item">
                              <span className="detail-label">Distancia</span>
                              <span className="detail-value">
                                {safeNumber(punto.distancia_recorrida).toFixed(1)} m
                              </span>
                            </div>
                          )}
                        </div>
                        
                        {punto.address && (
                          <div style={{ 
                            marginTop: '0.5rem', 
                            fontSize: '0.8rem', 
                            color: 'rgba(255, 255, 255, 0.6)' 
                          }}>
                            📍 {punto.address}
                          </div>
                        )}
                      </div>
                    ))}
                    
                    {trazabilidad.length > 10 && (
                      <div style={{ 
                        textAlign: 'center', 
                        padding: '1rem', 
                        color: 'rgba(255, 255, 255, 0.6)' 
                      }}>
                        Mostrando los últimos 10 puntos de {trazabilidad.length} total
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
